import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spinner } from '@nvision/ui';

import { InfiniteScroll } from '@/components/infinite-scroll';
import { Page } from '@/components/page';
import { NoData } from '@/modules/errors';

import { type FormRef, TariffsSearchFiltersForm } from '../../components/forms';
import { useTariffSearchFilters, useTariffs } from '../../hooks';
import { TariffCard } from './tariff-card';
import { TariffsControls } from './tariffs-controls';

import styles from './tariffs.module.css';

const SCROLL_OFFSET = 200;

export const Tariffs: React.FC = () => {
  const { clearFilters, customerKey, tariffsSearchParams, setFilters, formValues } = useTariffSearchFilters();
  const {
    data: tariffs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
  } = useTariffs(tariffsSearchParams);
  const { t } = useTranslation();

  const formRef = useRef<FormRef>({} as FormRef);
  const prevCustomerKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevCustomerKeyRef.current !== null && prevCustomerKeyRef.current !== customerKey && customerKey) {
      formRef.current?.reset();
      clearFilters();
    }

    if (customerKey) {
      prevCustomerKeyRef.current = customerKey;
    }
  }, [customerKey, clearFilters]);

  return (
    <Page>
      <Page.SideBar>
        <Page.SideBar.Header title="Tariff Search" />
        <Page.SideBar.Main>
          <TariffsSearchFiltersForm initialValues={formValues} formRef={formRef} onSubmit={setFilters} />
        </Page.SideBar.Main>
        <Page.SideBar.Footer>
          <Button secondary type="button" size="sm" onClick={() => formRef.current?.reset()}>
            {t('translation:default.buttons.clear')}
          </Button>

          <Button type="submit" size="sm" primary onPress={() => formRef.current?.submit()}>
            {t('translation:default.buttons.search')}
          </Button>
        </Page.SideBar.Footer>
      </Page.SideBar>
      <Page.Main>
        <TariffsControls />
        {isLoading || (isRefetching && !isFetchingNextPage) ? (
          <div className={styles.loading}>
            <Spinner size="lg" />
          </div>
        ) : tariffs?.items && tariffs?.items?.length > 0 ? (
          <InfiniteScroll
            className={styles.list}
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage}
            loadMore={fetchNextPage}
            scrollOffset={SCROLL_OFFSET}
          >
            {tariffs?.items.map((tariff) => (
              <TariffCard key={tariff.tariffKey} tariff={tariff} />
            ))}
          </InfiniteScroll>
        ) : (
          <NoData description="No data to display. Please select the filters from the left panel." />
        )}
      </Page.Main>
    </Page>
  );
};
